import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '../auth/types';
import ErrorBoundary from './ErrorBoundary';
import PageHeader from './shell/PageHeader';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import HomeWeatherCard from './home/HomeWeatherCard';
import ActivityFeed from './home/ActivityFeed';
import RecentSpraySessionsCard from './home/RecentSpraySessionsCard';
import RecentScoutingCard from './home/RecentScoutingCard';
import MyActiveOperatorSessionsCard from './home/MyActiveOperatorSessionsCard';
import {
  fetchActivity, fetchRecentSpraySessions, fetchRecentScouting,
  ActivityItem, TodaySession, TodayScoutingRecord,
} from '../services/activityService';
import { sprayService, Orchard } from '../services/sprayService';
import './home/Home.css';

interface Props {
  user: User;
}

const Home: React.FC<Props> = ({ user }) => {
  const { t } = useTranslation();

  useBreadcrumb([{ label: 'Citrus Synch' }]);

  const firstName = user?.name?.trim().split(/\s+/)[0] || '';
  const title = firstName
    ? t('home.welcome.title', { firstName })
    : t('home.welcome.titleFallback');

  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [activityErr, setActivityErr] = useState<string | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [sessions, setSessions] = useState<TodaySession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [scouting, setScouting] = useState<TodayScoutingRecord[]>([]);
  const [scoutingLoading, setScoutingLoading] = useState(true);

  const isSystemUser = user?.superadmin;

  useEffect(() => {
    if (isSystemUser) {
      setActivityLoading(false);
      setSessionsLoading(false);
      setScoutingLoading(false);
      return;
    }
    let cancelled = false;
    fetchActivity()
      .then(items => { if (!cancelled) setActivity(items); })
      .catch((e: Error) => { if (!cancelled) setActivityErr(e.message || 'error'); })
      .finally(() => { if (!cancelled) setActivityLoading(false); });
    fetchRecentSpraySessions()
      .then(items => { if (!cancelled) setSessions(items); })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setSessionsLoading(false); });
    sprayService.getOrchards()
      .then(list => { if (!cancelled) setOrchards(list); })
      .catch(() => undefined);
    fetchRecentScouting()
      .then(items => { if (!cancelled) setScouting(items); })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setScoutingLoading(false); });
    return () => { cancelled = true; };
  }, [isSystemUser]);

  return (
    <div className="home">
      <PageHeader title={title} meta={user.tenantName} />
      {!isSystemUser && (
        <>
          <section
            aria-label={t('home.weather.title')}
            data-testid="home-weather-section"
          >
            <ErrorBoundary moduleName="Home Weather">
              <HomeWeatherCard />
            </ErrorBoundary>
          </section>
          <div className="home__body">
            <div className="home__body-left">
              <ErrorBoundary moduleName="Home Activity">
                <ActivityFeed items={activity} loading={activityLoading} error={activityErr} />
              </ErrorBoundary>
            </div>
            <div className="home__body-right">
              <ErrorBoundary moduleName="My Active Sessions">
                <MyActiveOperatorSessionsCard />
              </ErrorBoundary>
              <ErrorBoundary moduleName="Recent Sessions">
                <RecentSpraySessionsCard items={sessions} loading={sessionsLoading} orchards={orchards} />
              </ErrorBoundary>
              <ErrorBoundary moduleName="Recent Scouting">
                <RecentScoutingCard items={scouting} loading={scoutingLoading} />
              </ErrorBoundary>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export { Home };
